import type {
  Condition,
  LeanVariant,
  FallbackCookies,
  FeatureFlagCondition,
  CookieCondition,
  SearchParamCondition,
  RandomizeCondition,
  CookieDateCondition,
} from '../types'
import { earlyGrowthbookScript } from 'growthbook/early-growthbook-script'
import { jsCookie } from 'growthbook/js-cookie-script'
import {
  WORDPRESS_LOGIN_COOKIE_NAME,
  USER_ID_COOKIE_NAME,
  SUBSCRIPTION_STATUS_VALUES,
  SUBSCRIPTION_STATUS_COOKIE_NAME,
  COUNTRY_COOKIE_NAME,
  GROWTHBOOK_DISABLE_ALL_FEATURES_COOKIE_NAME,
  GROWTHBOOK_ENABLED_FEATURES_COOKIE_NAME,
} from 'constants/cookies'
import { AMPLITUDE_DEVICE_ID_COOKIE_NAME } from '@repo/analytics/amplitude/constants'
import type { GrowthBook, BrowserCookieStickyBucketService } from 'growthbook/types'

declare global {
  interface Window {
    __gbInitialPayload?: string
    growthbook?: {
      GrowthBook?: typeof GrowthBook
      BrowserCookieStickyBucketService?: typeof BrowserCookieStickyBucketService
    }
    earlyGrowthBook?: GrowthBook
  }
}

/**
 * StaticVariantsHeadScripts - Required scripts for the StaticVariantsRenderer to function properly
 *
 * This component must be included in the `<head>` section of your document.
 * It provides the necessary scripts for variant selection and visibility,
 * along with a noscript fallback for when JavaScript is disabled.
 *
 * @returns {JSX.Element} The head scripts component
 */
export const StaticVariantsHeadScripts = () => (
  <>
    <EarlyGrowthbookScript />
    <TrimStateScript />
    <GlobalFunctionsScript />
    <StaticVariantsNoScript />
    <ForceReloadOnClientSideNavigationScript />
  </>
)

/**
 * Serialised with `.toString()` into an inline script, so the body can only use its parameters
 * and browser globals — never an import. Keep comments and the literal text `<` + `script`
 * out of the body: the HTML parser would split the inline script and try to execute the rest.
 *
 * `deviceType` is derived here by hand for the same reason (no `ua-parser-js`), and mirrors the
 * edge worker's `isMobile()` deliberately — if the two selectors disagreed, a user could be
 * served one variant at the edge and shown another on the client.
 */
export function initializeGrowthbook(
  wordpressLoginCookieName: string,
  countryCookieName: string,
  userIdCookieName: string,
  amplitudeDeviceIdCookieName: string,
  subscriptionStatusCookieName: string,
  allAccessValues: Record<string, string>,
  growthbookDisableCookieName: string,
  growthbookEnabledFeaturesCookieName: string,
) {
  try {
    const htmlFeatureFlags = window.__gbInitialPayload
    let cachedFeatureFlagsStringified = null

    try {
      cachedFeatureFlagsStringified = localStorage.getItem('gbFeaturesCache')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error, 'Error retrieving cached feature flags from localStorage')
    }

    let cachedFeatureFlags

    try {
      cachedFeatureFlags = cachedFeatureFlagsStringified
        ? JSON.parse(cachedFeatureFlagsStringified)[0][1].data
        : undefined
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error, 'Error parsing cached feature flags')
    }

    const payload = cachedFeatureFlags || htmlFeatureFlags

    const getCookie = (cookieName: string) => {
      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith(cookieName + '='))
          ?.split('=')[1]

        return cookieValue ? decodeURIComponent(cookieValue) : undefined
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error, 'Error retrieving cookie value for', cookieName)
      }
    }

    const isGrowthBookDisabled = () => {
      return (
        getCookie(growthbookDisableCookieName) === 'true' ||
        Boolean(getCookie(growthbookEnabledFeaturesCookieName))
      )
    }

    const getEnabledFeatures = () => {
      const featureCookie = getCookie(growthbookEnabledFeaturesCookieName)

      if (!featureCookie?.trim()) {
        return {}
      }

      try {
        const parsed = JSON.parse(featureCookie)
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed
        }
        return {}
      } catch (error) {
        return {}
      }
    }

    const isLoggedIn = !!getCookie(wordpressLoginCookieName)
    const country = getCookie(countryCookieName)
    const userId = getCookie(userIdCookieName)
    const deviceId = getCookie(amplitudeDeviceIdCookieName)
    const hasAllAccess = Object.values(allAccessValues).includes(
      getCookie(subscriptionStatusCookieName) ?? '',
    )

    const userAgentData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
      .userAgentData
    const deviceType =
      userAgentData?.mobile || /mobile|android|iphone/i.test(navigator.userAgent)
        ? 'mobile'
        : 'desktop'

    const GrowthBook = window?.growthbook?.GrowthBook
    const BrowserCookieStickyBucketService = window?.growthbook?.BrowserCookieStickyBucketService

    if (!GrowthBook) {
      throw new Error('GrowthBook is not defined')
    }
    if (!BrowserCookieStickyBucketService) {
      throw new Error('BrowserCookieStickyBucketService is not defined')
    }

    const isDisabled = isGrowthBookDisabled()

    if (!payload && !isDisabled) {
      throw new Error('No feature flags found')
    }

    window.earlyGrowthBook = new GrowthBook({
      apiHost: 'https://cdn.growthbook.io',
      attributes: {
        id: userId ?? null,
        deviceId,
        isLoggedIn: isLoggedIn ?? false,
        country,
        hasAllAccess: hasAllAccess ?? false,
        deviceType,
        language: document.documentElement.lang,
        referrer: document.referrer,
      },
      stickyBucketService: new BrowserCookieStickyBucketService({
        jsCookie: window.Cookies,
      }),
    }).initSync({
      payload: isDisabled ? { features: getEnabledFeatures() } : payload,
      streaming: false,
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, 'Error initializing early growthbook')
  }
}

const EarlyGrowthbookScript = () => (
  <script
    id="static-variant-head-early-growthbook"
    suppressHydrationWarning
    dangerouslySetInnerHTML={{
      __html: `
      ${earlyGrowthbookScript}
      ${jsCookie}

      ;(${initializeGrowthbook.toString()})(
        ${JSON.stringify(WORDPRESS_LOGIN_COOKIE_NAME)},
        ${JSON.stringify(COUNTRY_COOKIE_NAME)},
        ${JSON.stringify(USER_ID_COOKIE_NAME)},
        ${JSON.stringify(AMPLITUDE_DEVICE_ID_COOKIE_NAME)},
        ${JSON.stringify(SUBSCRIPTION_STATUS_COOKIE_NAME)},
        ${JSON.stringify(SUBSCRIPTION_STATUS_VALUES)},
        ${JSON.stringify(GROWTHBOOK_DISABLE_ALL_FEATURES_COOKIE_NAME)},
        ${JSON.stringify(GROWTHBOOK_ENABLED_FEATURES_COOKIE_NAME)}
      )
        `,
    }}
  />
)

const TrimStateScript = () => (
  <script
    id="static-variant-head-trim-state"
    suppressHydrationWarning
    dangerouslySetInnerHTML={{
      __html: `window.__staticVariantsTrimmed = false`,
    }}
  />
)

const StaticVariantsNoScript = () => (
  <noscript id="static-variant-head-noscript">
    <style>{`.render-default { display: block !important; }`}</style>
  </noscript>
)

// This is a workaround to ensure that the page is reloaded when the user navigates back to the page from a
// client side navigation.
// Using window.location.href = window.location.href to do a reload instead of window.location.reload()
// so it reloads the page scrolling to the top of the page.
const ForceReloadOnClientSideNavigationScript = () => (
  <script
    id="static-variant-head-navigation"
    suppressHydrationWarning
    dangerouslySetInnerHTML={{
      __html: getNavigationScript(),
    }}
  />
)

const getNavigationScript = () => `
  window.addEventListener("popstate", (e) => {
    window.location.href = window.location.href;
  });
`

const GlobalFunctionsScript = () => {
  return (
    <script
      id={'static-variant-head-global-functions'}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: generateStaticVariantsScript(),
      }}
    />
  )
}

const generateStaticVariantsScript = (): string => `
  const randomValue = Math.random() * (100 - 1) + 1;
   window.__staticVariantSelector = (${selectVariant.toString()});
   window.__makeVariantVisible = (${makeVariantVisible.toString()});`

const randomValue = Math.random() * (100 - 1) + 1

/* eslint-disable no-console*/
// this selector is replicated in cloudflare workers
export function selectVariant(staticVariantsData: {
  variants: LeanVariant[]
  fallbackCookies?: FallbackCookies
}) {
  const { variants, fallbackCookies } = staticVariantsData
  function getCookieValue(cookieName: string) {
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(cookieName + '='))
        ?.split('=')[1]

      return cookieValue ? decodeURIComponent(cookieValue) : undefined
    } catch (error) {
      console.error(error, `Error retrieving cookie value for ${cookieName}`)
    }
  }

  function isCookieConditionSatisfied(condition: CookieCondition) {
    // Normalize null, undefined values to undefined in expected values
    const normalizeValue = (value: string | undefined) =>
      value === null || value === undefined || value === '' ? undefined : value

    const cookieValue = normalizeValue(
      getCookieValue(condition.cookieName) || fallbackCookies?.[condition.cookieName],
    )

    if (condition.operator === 'EQUALS') {
      return cookieValue === normalizeValue(condition.expectedValue)
    }
    if (condition.operator === 'NOT_EQUALS') {
      return cookieValue !== normalizeValue(condition.expectedValue)
    }
    if (condition.operator === 'STARTS_WITH') {
      const normalizedExpected = normalizeValue(condition.expectedValue)
      return !!normalizedExpected && !!cookieValue && cookieValue.startsWith(normalizedExpected)
    }
    if (condition.operator === 'NOT_STARTS_WITH') {
      const normalizedExpected = normalizeValue(condition.expectedValue)
      return !!normalizedExpected && !!cookieValue && !cookieValue.startsWith(normalizedExpected)
    }
    if (condition.operator === 'IN') {
      return condition.expectedValue.map(normalizeValue).includes(cookieValue)
    }
    if (condition.operator === 'NOT_IN') {
      return !condition.expectedValue.map(normalizeValue).includes(cookieValue)
    }
    if (condition.operator === 'EXISTS') {
      return !!cookieValue
    }
    if (condition.operator === 'NOT_EXISTS') {
      return !cookieValue
    }
    return false
  }

  function isFeatureFlagConditionSatisfied(condition: FeatureFlagCondition) {
    const growthbookInstance = window?.globalGrowthBook || window?.earlyGrowthBook

    let featureFlagValue: boolean | number | undefined = undefined

    if (typeof condition.expectedValue === 'boolean') {
      featureFlagValue = growthbookInstance?.isOn(condition.featureName)
    } else {
      featureFlagValue = growthbookInstance?.getFeatureValue(condition.featureName)
    }

    return condition.expectedValue === featureFlagValue
  }

  function isSearchParamConditionSatisfied(condition: SearchParamCondition) {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get(condition.searchParamName) === condition.expectedValue
  }

  function isCookieDateConditionSatisfied(condition: CookieDateCondition) {
    // Normalize null, undefined values to undefined in expected values
    const normalizeValue = (value: string | undefined) =>
      value === null || value === undefined || value === '' ? undefined : value

    const cookieValue = normalizeValue(
      getCookieValue(condition.cookieName) || fallbackCookies?.[condition.cookieName],
    )

    const cookieValueDate = cookieValue ? new Date(cookieValue) : null

    if (!cookieValueDate) {
      return false
    }

    const expectedDate = new Date(condition.expectedValue)

    if (condition.operator === 'BEFORE') {
      return cookieValueDate < expectedDate
    }
    if (condition.operator === 'AFTER') {
      return cookieValueDate > expectedDate
    }
  }

  function isRandomizationConditionSatisfied(condition: RandomizeCondition) {
    const [min, max] = condition.interval
    if (randomValue <= max && randomValue >= min) {
      return true
    }

    return false
  }

  function isSatisfied(condition: Condition) {
    if (condition.type === 'cookie') {
      return isCookieConditionSatisfied(condition)
    }

    if (condition.type === 'cookie-date') {
      return isCookieDateConditionSatisfied(condition)
    }

    if (condition.type === 'feature-flag') {
      return isFeatureFlagConditionSatisfied(condition)
    }

    if (condition.type === 'search-param') {
      return isSearchParamConditionSatisfied(condition)
    }

    if (condition.type === 'randomize') {
      return isRandomizationConditionSatisfied(condition)
    }
  }

  let selectedVariant: LeanVariant | null = null
  let defaultVariant: LeanVariant | null = null
  try {
    defaultVariant = variants.find(function (variant) {
      return variant.isDefault
    })!

    variants.forEach(function (variant) {
      if (selectedVariant) {
        return
      }

      if (variant.conditions.every(isSatisfied)) {
        selectedVariant = variant
      }
    })

    return (selectedVariant || defaultVariant).id
  } catch (error) {
    console.error(error, 'Error in static variant script')
    return defaultVariant?.id
  }
}

export function makeVariantVisible(variantId: string | undefined, renderingId: string) {
  if (!variantId) {
    console.error('No variant id found')
    return
  }

  // Remove any previously selected variant for this rendering context
  // to prevent multiple variants from being visible simultaneously
  // (can happen when earlyGrowthBook and globalGrowthBook disagree on variant assignment)
  document
    .querySelectorAll('.static-variant-visible-style[data-rendering-id="' + renderingId + '"]')
    .forEach(function (el) {
      el.remove()
    })

  const style = document.createElement('style')
  style.id = `static-variant-visible-style-${renderingId}-${variantId}`
  style.className = `static-variant-visible-style`
  // oxlint-disable-next-line unicorn/prefer-dom-node-dataset
  style.setAttribute('data-rendering-id', renderingId)
  // oxlint-disable-next-line unicorn/prefer-dom-node-dataset
  style.setAttribute('data-variant-id', variantId)

  style.textContent = `.static-variant-${renderingId}-${variantId} { display: block !important; }`

  document.head.appendChild(style)
}
/* eslint-enable no-console*/
